// Rich text editor controller.
//
// Two engines behind one public API.
//
// `lexxy` (the default) wraps the <lexxy-editor> custom element the lexxy gem
// defines. The gem owns the editing surface, the toolbar and the formatting
// commands; this controller owns everything around it:
//
//   • the TOOLBAR ALLOW-LIST. Lexxy builds its full toolbar in JavaScript, so a
//     server-rendered subset is impossible — the component publishes the button
//     names it wants as a value and the controller hides the rest once the
//     element upgrades. `hidden` attributes only, no injected <style>, so the
//     technique survives a strict CSP.
//   • the EVENT SURFACE. Consumers bind to ui--text-editor:change / :focus /
//     :blur and never to the gem's own lexxy:* events, so the Lexxy dependency
//     stops at this file.
//   • the PUBLIC API below, which is how the Work kit (comments, issue
//     composer) reads and writes the editor. Nothing outside this controller
//     should touch the editor element or a hidden input directly — that
//     coupling is exactly what broke when the contenteditable went away.
//   • the TURBO FRAME re-initialisation workaround, scoped to this element (the
//     host carries a document-wide copy that can retire once this ships).
//   • the WRITING-DIRECTION toggle. Lexical marks each block `dir="auto"` and
//     detects direction from its first strong character, which is right until
//     an Arabic paragraph opens with a Latin product name or a digit. The
//     toggle pins the whole FIELD instead — and it has to be the field,
//     because Lexxy serializes no direction at all: setDirection, setStyle and
//     setFormat every one of them reconcile into the DOM and none of them
//     reaches `editor.value`, and importing `<p dir="rtl">` hands back a bare
//     `<p>`. So the choice rides the element and the HOST stores it beside the
//     body; it cannot live inside the body.
//
// `basic` is the pre-Lexxy contenteditable + execCommand editor, kept verbatim
// so existing surfaces keep working during rollout. It is DEPRECATED and goes
// away with the component's `engine:` prop in the next minor.

import { Controller } from "@hotwired/stimulus"

// Toolbar controls that are dropdown wrappers rather than plain buttons. Their
// visibility is decided by the name on the trigger INSIDE them, and the whole
// wrapper is hidden — a bare wrapper is still a flex item and would leave a gap.
const DROPDOWN_TAGS = "lexxy-toolbar-dropdown, lexxy-highlight-dropdown, lexxy-link-dropdown"

// The React editor's zoom bounds, kept in step with PAGE_FORMATS/ZOOM_* on the
// Ruby side.
// The React editor's `getDefaultContent`, verbatim in shape: what the editor
// opens on when the stored JSON will not parse, so a bad row shows a sentence
// instead of throwing.
const invalidContentState = (text) => JSON.stringify({
  root: {
    children: [{
      children: [{ detail: 0, format: 0, mode: "normal", style: "", text, type: "text", version: 1 }],
      direction: "ltr", format: "", indent: 0, type: "paragraph", version: 1, textFormat: 0,
    }],
    direction: "ltr", format: "", indent: 0, type: "root", version: 1,
  },
})

// Marks every control the component adds to the gem's toolbar: exempt from
// the allow-list pass, and the hook the icon-fill reset keys off.
const OWN_CONTROL_CLASS = "ui-text-editor__toolbar-control"

// Lexical's text-format bitmask, the half of it we drive ourselves.
const TEXT_FORMAT_BITS = { subscript: 32, superscript: 64 }

const RTL_CLASS = "ui-text-editor--rtl"
const LTR_CLASS = "ui-text-editor--ltr"

const ZOOM_MIN = 50
const ZOOM_MAX = 150
const ZOOM_STEP = 10
const ZOOM_DEFAULT = 100

// The desk the React editor keeps either side of the sheet when it scales a
// page down to fit: `containerWidth / (pageWidthPx + 80)`.
const DESK_GUTTER = 80

// The gap the floating link panel keeps from the words it is about, and from
// the edges of the window when it has to be nudged back inside.
const LINK_PANEL_GAP = 8

// `- [ ] ` and its spellings, which the React editor turns into a check item
// and the gem's markdown transformers do not cover. Anchored at both ends: a
// line that already has prose in it is someone writing, not reaching for a
// shortcut.
//
// TWO forms, because the gem gets there first. Typing `- ` is its own markdown
// shortcut, so by the time the brackets are typed the block is usually already
// a bullet item and all that is left on the line is `[ ]`. The dash form still
// has to work for the cases where that transform did not run.
const CHECK_LIST_SHORTCUT = /^[-*] ?\[[ xX]?\]$/
const CHECK_LIST_SHORTCUT_IN_LIST = /^\[[ xX]?\]$/

export default class extends Controller {
  static targets = ["editor", "hidden", "toolbar", "toolbarButton", "directionToggle", "tool", "preview", "editShell", "pageFormat", "zoomGroup", "zoomLevel", "align", "toolbarSource", "fontControl", "fontValue", "blockFormat", "blockFormatValue", "insertImageDialog", "insertImageFile", "insertImageAlt", "insertImageConfirm", "wordCount", "insertLinkDialog", "insertLinkUrl", "insertLinkText", "insertLinkUnlink"]
  static values = {
    disabled: Boolean,
    engine: String,
    name: String,
    toolbar: Array,
    directionToggle: Boolean,
    tools: Boolean,
    editableInPlace: Boolean,
    pageChrome: Boolean,
    valueFormat: String,
    invalidContentText: String,
  }

  connect() {
    if (this.isLexxy) {
      this.connectLexxy()
    } else {
      this.syncHiddenInput()
      this.setupPlaceholder()
      this.setupKeyboardShortcuts()
    }
  }

  disconnect() {
    this.teardownLexxy?.()
  }

  get isLexxy() {
    // The value is absent on the read-only branch of older markup; `lexxy` is
    // the component's default, so treat anything that is not `basic` as Lexxy.
    return this.engineValue !== "basic"
  }

  // The <lexxy-editor> element, or null on the read-only branch (which renders
  // sanitized HTML and a hidden input, with no editor at all).
  get editorElement() {
    if (!this.hasEditorTarget) return null
    return this.editorTarget.tagName.toLowerCase() === "lexxy-editor" ? this.editorTarget : null
  }

  // ---------------------------------------------------------------- public API

  get isLexicalJson() {
    return this.valueFormatValue === "lexical_json"
  }

  // The document as the editor's own state tree, stringified — the React
  // editor's `JSON.stringify(state.toJSON())`.
  //
  // Null on an HTML field, even though the tree is reachable there too: on
  // that format the document IS the HTML, and handing a host a tree beside it
  // invites storing the wrong one.
  getJSON() {
    if (!this.isLexicalJson) return null
    const editor = this.editorElement?.editor
    if (!editor) return null
    try {
      return JSON.stringify(editor.getEditorState().toJSON())
    } catch {
      return null
    }
  }

  // The document as HTML — what the field submits.
  getHTML() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.value ?? ""
      return this.hasHiddenTarget ? this.hiddenTarget.value : ""
    }
    return this.hasEditorTarget ? this.editorTarget.innerHTML : ""
  }

  // The document as plain text — for drafts, length checks and announcements.
  getText() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.toString() ?? ""
      return this.stripTags(this.hasHiddenTarget ? this.hiddenTarget.value : "")
    }
    if (!this.hasEditorTarget) return ""
    return this.editorTarget.innerText || this.editorTarget.textContent || ""
  }

  setValue(html) {
    const value = html ?? ""
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) editor.value = value
      if (this.hasHiddenTarget) this.hiddenTarget.value = value
      return
    }
    if (this.hasEditorTarget) this.editorTarget.innerHTML = value
    this.syncHiddenInput()
  }

  clear() {
    this.setValue("")
  }

  focus() {
    if (this.isLexxy) {
      this.editorElement?.focus()
      return
    }
    if (this.hasEditorTarget) this.editorTarget.focus()
  }

  isEmpty() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.isEmpty
      return this.getText().trim() === ""
    }
    return this.getText().trim() === "" && !this.editorTarget?.querySelector("img")
  }

  // ------------------------------------------------------------- lexxy engine

  connectLexxy() {
    const editor = this.editorElement

    this.onLexxyInitialize = () => {
      this.applyToolbarAllowList()
      this.placeDirectionToggle()
      this.loadLexicalJson()
      this.observeToolbarState()
    }
    this.onLexxyChange = () => {
      this.syncLexicalJson()
      this.refreshBlockFormatLabel()
      this.emit("change", { html: this.getHTML(), text: this.getText(), json: this.getJSON() })
    }
    this.onLexxyFocus = () => this.emit("focus")
    this.onLexxyBlur = () => this.emit("blur")
    this.onFrameLoad = () => this.reinitializeStalledEditor()
    // Escape leaves an in-place edit. Bound on the element rather than through
    // data-action so it catches the key wherever it is pressed inside the
    // editor, which is where the user actually is.
    this.onKeydown = (event) => {
      if (this.maybeCheckListShortcut(event)) return
      if (event.key !== "Escape" || !this.editingInPlace) return
      event.preventDefault()
      this.cancelEdit()
    }

    if (editor) {
      editor.addEventListener("lexxy:initialize", this.onLexxyInitialize)
      editor.addEventListener("lexxy:change", this.onLexxyChange)
      editor.addEventListener("lexxy:focus", this.onLexxyFocus)
      editor.addEventListener("lexxy:blur", this.onLexxyBlur)
    }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.addEventListener("keydown", this.onKeydown)
    // A plain click moves the caret without producing an editor update, so
    // the DOM event backs the update listener up.
    this.onSelectionChange = () => this.refreshToolbarState()
    document.addEventListener("selectionchange", this.onSelectionChange)

    // An editor that opens already paged — `page_format: :a4` — has never been
    // through changePageFormat, so the fit has to be taken here too.
    this.watchDeskWidth()
    this.applyScale()

    this.teardownLexxy = () => {
      if (editor) {
        editor.removeEventListener("lexxy:initialize", this.onLexxyInitialize)
        editor.removeEventListener("lexxy:change", this.onLexxyChange)
        editor.removeEventListener("lexxy:focus", this.onLexxyFocus)
        editor.removeEventListener("lexxy:blur", this.onLexxyBlur)
      }
      document.removeEventListener("turbo:frame-load", this.onFrameLoad)
      this.element.removeEventListener("keydown", this.onKeydown)
      document.removeEventListener("selectionchange", this.onSelectionChange)
      this._stopObservingState?.()
      this._stopObservingState = null
      this.unwatchDeskWidth()
      this.unwatchLinkPanel()
      this.teardownLexxy = null
    }

    // The element can upgrade BEFORE Stimulus connects this controller: the
    // gem's module is loaded in <head> while the controller bundle is deferred.
    // lexxy:initialize has already fired by then and never fires again, so the
    // allow-list has to be applied on the spot.
    this.applyToolbarAllowList()
    this.placeDirectionToggle()
    this.loadLexicalJson()
    this.observeToolbarState()
  }

  // ------------------------------------------------------- lexical json

  // The element parses `value` as HTML, so a JSON document cannot ride the
  // attribute — it comes in through the hidden input and is installed as the
  // editor state, which is how the React composer takes `initialState`.
  // Runs from BOTH connect and lexxy:initialize, because either can come
  // first: the gem's module is loaded in <head> while the controller bundle
  // is deferred, so the element sometimes upgrades before Stimulus and
  // sometimes long after. Loading once is the point — a second pass would
  // reinstall the stored tree over whatever had been typed since.
  // === INSERT LINK ===
  //
  // The gem's link control is a dropdown holding one bare URL field. The
  // React editor asks for the URL and the text the reader will see, so this
  // is ours, and it opens with the current selection already in the text
  // field the way that one does.

  openInsertLink(event) {
    event?.preventDefault()
    const panel = this.insertLinkPanel()
    if (!panel) return

    // Read the selection NOW. Opening the modal moves focus to it and the
    // DOM selection goes with it, so by the time Confirm is pressed there is
    // nothing left to read. Lexical's own selection does survive, which is
    // what actually places the link.
    this.linkSelectionAtOpen = this.selectedText()

    // Editing rather than inserting: with the caret inside a link, the dialog
    // opens on that link so it can be corrected instead of being a second
    // one laid on top.
    const existing = this.linkAtCaret()
    this.editingLinkKey = existing?.key ?? null

    if (this.hasInsertLinkUrlTarget) this.insertLinkUrlTarget.value = existing?.url ?? ""
    if (this.hasInsertLinkTextTarget) {
      this.insertLinkTextTarget.value = existing?.text ?? this.linkSelectionAtOpen
    }
    // Nothing to remove when the caret is not in a link, so the action that
    // removes one has no business being offered.
    if (this.hasInsertLinkUnlinkTarget) this.insertLinkUnlinkTarget.hidden = !existing

    // Where the words ARE, measured before the panel opens and takes the
    // focus away from them.
    this.linkAnchor = this.linkAnchorRect()
    if (!panel.open) panel.show()
    this.placeLinkPanel()
    this.watchLinkPanel()

    // show() focuses the panel's first focusable, which is the close button —
    // the URL is what the reader came to type. No scroll: the panel is fixed
    // beside the words, and scrolling to it would move them out from under it.
    if (this.hasInsertLinkUrlTarget) this.insertLinkUrlTarget.focus({ preventScroll: true })
  }

  closeInsertLink(event) {
    event?.preventDefault()
    this.closeLinkPanel()
  }

  closeLinkPanel() {
    const panel = this.insertLinkPanel()
    if (!panel?.open) return
    this.unwatchLinkPanel()
    panel.close()
  }

  // The rect the panel hangs off: what the reader selected, or — with a
  // collapsed caret, which has no width and sometimes no rect at all — the
  // control they pressed, so the panel never lands in the top-left corner.
  linkAnchorRect() {
    const selection = this.editorElement?.ownerDocument?.getSelection?.()
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    if (selection?.rangeCount && content?.contains(selection.anchorNode)) {
      const rect = selection.getRangeAt(0).getBoundingClientRect()
      if (rect.width || rect.height) return rect
    }
    return this.element.querySelector("[data-control='link']")?.getBoundingClientRect() ?? null
  }

  // Fixed, not absolute: the panel is a sibling of <lexxy-editor>, and the
  // paged desk around the editor scrolls and carries a `zoom`. Positioning it
  // against the viewport is what keeps it out of both.
  placeLinkPanel() {
    const panel = this.insertLinkPanel()
    const rect = this.linkAnchor
    if (!panel) return

    panel.style.position = "fixed"
    if (!rect) return

    const width = panel.offsetWidth
    const height = panel.offsetHeight
    const room = { x: window.innerWidth, y: window.innerHeight }

    let top = rect.bottom + LINK_PANEL_GAP
    if (top + height > room.y - LINK_PANEL_GAP) {
      const above = rect.top - height - LINK_PANEL_GAP
      top = above >= LINK_PANEL_GAP ? above : Math.max(LINK_PANEL_GAP, room.y - height - LINK_PANEL_GAP)
    }

    const left = Math.max(LINK_PANEL_GAP, Math.min(rect.left, room.x - width - LINK_PANEL_GAP))

    panel.style.top = `${top}px`
    panel.style.left = `${left}px`
  }

  // Nothing is inert behind a panel, so it has to be dismissible the way a
  // panel is: Escape, a click past it, or the page moving out from under it.
  // Escape is handled here because `show()` — unlike `showModal()` — gives a
  // <dialog> no native cancel.
  watchLinkPanel() {
    if (this._linkPanelWatchers) return

    const onPointerDown = (event) => {
      const panel = this.insertLinkPanel()
      if (panel?.contains(event.target)) return
      this.closeLinkPanel()
    }
    const onKeydown = (event) => {
      if (event.key === "Escape") this.closeLinkPanel()
    }
    const onReflow = () => this.placeLinkPanel()

    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeydown)
    window.addEventListener("resize", onReflow)
    window.addEventListener("scroll", onReflow, true)
    this._linkPanelWatchers = () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeydown)
      window.removeEventListener("resize", onReflow)
      window.removeEventListener("scroll", onReflow, true)
    }
  }

  unwatchLinkPanel() {
    this._linkPanelWatchers?.()
    this._linkPanelWatchers = null
  }

  confirmInsertLink(event) {
    event?.preventDefault()

    // Normalised before anything else, and it is the normalised value that
    // gets stored: checking one string and saving another is how the strip
    // gets bypassed.
    const url = this.normaliseLinkUrl(this.insertLinkUrlTarget?.value ?? "")
    if (!url) return

    // A `javascript:` href in a stored document is an XSS vector the moment
    // anything renders it, and this dialog takes free text. The gem's control
    // checked nothing either, so this is not a regression being fixed but a
    // hole not being inherited. The dialog stays open on a refusal — the URL
    // is still there to correct.
    if (!this.isSafeLinkUrl(url)) {
      this.emit("error", { reason: "unsafe-url", url })
      return
    }

    const text = this.hasInsertLinkTextTarget ? this.insertLinkTextTarget.value.trim() : ""
    const contents = this.editorElement?.contents
    if (!contents?.createLink) return

    this.closeLinkPanel()

    // `createLinkWithSelectedText` wraps what was selected and keeps it as the
    // link's text; `createLink` inserts a new link whose text IS the url.
    const selected = this.linkSelectionAtOpen
    const editingKey = this.editingLinkKey
    this.linkSelectionAtOpen = ""
    this.editingLinkKey = null

    if (editingKey) {
      this.updateLink(editingKey, url, text)
      this.refreshWordCount()
      this.emit("change", { html: this.getHTML(), text: this.getText(), json: this.getJSON() })
      return
    }

    if (selected && text === selected && contents.createLinkWithSelectedText) {
      contents.createLinkWithSelectedText(url)
    } else {
      const before = this.linkKeys()
      contents.createLink(url)
      if (text && text !== url) this.setLinkTextForNewLink(before, text)
    }

    this.refreshWordCount()
    this.emit("change", { html: this.getHTML(), text: this.getText(), json: this.getJSON() })
  }

  // The node API has no unlink, so the removal goes through the gem's own
  // `unlink` command. Not by clicking a button of the gem's: there is no
  // `[data-command='unlink']` anywhere in its toolbar (its dropdown's button
  // is `value="unlink"` and wired by the dropdown itself), so the click this
  // used to make found nothing and Unlink silently did nothing. The system
  // test drives this against the real gem.
  removeLink(event) {
    event?.preventDefault()
    this.closeLinkPanel()
    this.runGemCommand("unlink")
  }

  // Browsers REMOVE ASCII tab, newline and carriage return from a URL before
  // they parse it, anywhere in the string — so `jav&#9;ascript:alert(1)` is
  // `javascript:alert(1)` by the time it is an href. Checking the raw text
  // matches no scheme at all and waves it through; the document then stores
  // it and re-parses it on render. Strip first, check second, and store what
  // was checked.
  normaliseLinkUrl(url) {
    return url.replace(/[\t\n\r]/g, "").trim()
  }

  // Anything without a scheme is relative and fine — a path, a fragment, a
  // bare domain someone typed. Anything WITH one has to be a scheme that
  // navigates rather than executes.
  isSafeLinkUrl(url) {
    const scheme = this.normaliseLinkUrl(url).match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase()
    if (!scheme) return true
    return ["http", "https", "mailto", "tel"].includes(scheme)
  }

  // The link the caret sits in, if any. `ed.selection.nearestNodeOfType` takes
  // a node CLASS and tests it with `instanceof`, and that class is the gem's
  // to import, not ours — so this walks up from the text node under the caret
  // instead, which reaches the link in one hop.
  linkAtCaret() {
    const editor = this.editorElement?.editor
    if (!editor) return null

    let found = null
    try {
      editor.read(() => {
        let node = this.editorElement?.selection?.selectedNodeWithOffset?.()?.node
        let hops = 0
        while (node && node.getType?.() !== "link" && hops < 6) {
          node = node.getParent?.()
          hops += 1
        }
        if (node?.getType?.() === "link") {
          found = { key: node.getKey(), url: node.getURL?.() ?? "", text: node.getTextContent?.() ?? "" }
        }
      })
    } catch {
      // Only reachable inside an editor context, and only once the element
      // has upgraded; before that there is no link to find anyway.
    }
    return found
  }

  updateLink(key, url, text) {
    const editor = this.editorElement?.editor
    if (!editor) return

    try {
      editor.update(() => {
        const node = editor.getEditorState()._nodeMap.get(key)
        if (!node) return
        node.setURL?.(url)
        const child = node.getChildren?.()[0]
        if (text && child?.setTextContent) child.setTextContent(text)
      })
    } catch {
      // The link keeps whatever it had, which is no worse than before.
    }
  }

  // Identified by the key that was NOT there before, rather than by its URL:
  // linking to an address that already appears earlier in the document would
  // otherwise rewrite THAT link's text and leave the new one reading as its
  // own address. The key `createLink` returns cannot be used — it does not
  // resolve against the node map once its own update has committed.
  setLinkTextForNewLink(keysBefore, text) {
    const editor = this.editorElement?.editor
    if (!editor) return

    try {
      editor.update(() => {
        const fresh = this.linkNodes(editor).find((node) => !keysBefore.has(node.getKey()))
        const child = fresh?.getChildren?.()[0]
        if (child?.setTextContent) child.setTextContent(text)
      })
    } catch {
      // Worst case the link reads as its own URL, which is what the gem
      // would have produced on its own.
    }
  }

  linkKeys() {
    const editor = this.editorElement?.editor
    if (!editor) return new Set()

    const keys = new Set()
    try {
      editor.read(() => this.linkNodes(editor).forEach((node) => keys.add(node.getKey())))
    } catch {
      // No editor context yet; an empty set simply means "everything is new".
    }
    return keys
  }

  linkNodes(editor) {
    const root = editor.getEditorState()._nodeMap.get("root")
    const found = []
    const walk = (node) => {
      if (node.getType?.() === "link") found.push(node)
      if (node.getChildren) node.getChildren().forEach(walk)
    }
    if (root) walk(root)
    return found
  }


  selectedText() {
    const selection = this.editorElement?.ownerDocument?.getSelection?.()
    if (!selection || selection.isCollapsed) return ""
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    if (!content || !content.contains(selection.anchorNode)) return ""
    return selection.toString().trim()
  }

  insertLinkPanel() {
    return this.hasInsertLinkDialogTarget ? this.insertLinkDialogTarget : null
  }

  // === INSERT IMAGE ===
  //
  // The React editor does not hand an image straight to the OS picker: it asks
  // for the file and its alt text first. The gem's `uploadImage` command has no
  // room for that, so the control is ours and we call the gem's own upload
  // pipeline — `contents.uploadFiles`, exactly what its button reaches — once
  // both are in hand.

  openInsertImage(event) {
    event?.preventDefault()
    const modal = this.insertImageModal()
    if (!modal) return
    this.resetInsertImage()
    modal.open()
  }

  confirmInsertImage(event) {
    event?.preventDefault()

    // The target IS the file input — Ui::FileInputComponent puts the data
    // attributes on the control itself, not on a wrapper around it.
    const file = this.insertImageFileTarget?.files?.[0]
    if (!file) return

    const alt = this.hasInsertImageAltTarget ? this.insertImageAltTarget.value.trim() : ""
    this.insertImageModal()?.close()

    const contents = this.editorElement?.contents
    if (!contents?.uploadFiles) return

    if (alt) this.queueAltText(file.name, alt)
    contents.uploadFiles([file], { selectLast: true })
  }

  // Applying the alt text straight after `uploadFiles` does not survive. The
  // upload inserts a PLACEHOLDER node, and when the blob lands the gem swaps
  // it for the real one in `toAttachmentNode()`:
  //
  //   new ActionTextAttachmentNode({ ...this.uploadNode, ...propertiesFromBlob })
  //
  // where `propertiesFromBlob` carries `altText: blob.filename` and, spread
  // second, overwrites whatever the placeholder held. The typed description
  // was replaced by the file name every time.
  //
  // So the alt is held until the swap and written to the node that replaced
  // it, matched on the file name the gem itself stamped.
  queueAltText(fileName, alt) {
    this.pendingAltText ||= new Map()
    this.pendingAltText.set(fileName, alt)
    this.watchForUploadedAttachments()
  }

  watchForUploadedAttachments() {
    if (this.altTextWatcher) return
    const editor = this.editorElement?.editor
    if (!editor?.registerUpdateListener) return

    this.altTextWatcher = editor.registerUpdateListener(() => {
      if (!this.pendingAltText?.size) return
      this.applyPendingAltText(editor)
    })
  }

  applyPendingAltText(editor) {
    const claimed = []

    editor.getEditorState().read(() => {
      this.eachAttachmentNode(editor, (node) => {
        const json = node.exportJSON?.()
        if (!json || json.type !== "action_text_attachment") return

        const wanted = this.pendingAltText.get(json.fileName)
        if (wanted === undefined || json.altText === wanted) return
        claimed.push([node.getKey(), json.fileName, wanted])
      })
    })

    if (!claimed.length) return

    // Claim before writing: the write is itself an update, and re-entering
    // with the same entry still pending would loop.
    claimed.forEach(([, fileName]) => this.pendingAltText.delete(fileName))

    editor.update(() => {
      claimed.forEach(([key, , wanted]) => {
        const node = editor.getEditorState()._nodeMap.get(key)
        if (node) node.getWritable().altText = wanted
      })
    })

    // The node's own `updateDOM` does not re-stamp the attribute when only
    // altText changed, so the image on screen would keep announcing the file
    // name to a screen reader even though the stored value is right.
    claimed.forEach(([key, , wanted]) => {
      editor.getElementByKey(key)?.querySelector("img")?.setAttribute("alt", wanted)
    })
  }

  eachAttachmentNode(editor, visit) {
    const root = editor.getEditorState()._nodeMap.get("root")
    const walk = (node) => {
      if (node.getType && /attachment/i.test(node.getType())) visit(node)
      if (node.getChildren) node.getChildren().forEach(walk)
    }
    if (root) walk(root)
  }

  resetInsertImage() {
    if (this.hasInsertImageFileTarget) this.insertImageFileTarget.value = ""
    if (this.hasInsertImageAltTarget) this.insertImageAltTarget.value = ""
  }

  insertImageModal() {
    if (!this.hasInsertImageDialogTarget) return null
    const element = this.insertImageDialogTarget.querySelector("[data-controller~='ui--modal']")
    if (!element) return null
    return this.application.getControllerForElementAndIdentifier(element, "ui--modal")
  }

  loadLexicalJson() {
    if (!this.isLexicalJson || !this.hasHiddenTarget || this._jsonLoaded) return
    const editor = this.editorElement?.editor
    if (!editor) return

    const stored = this.hiddenTarget.value
    if (!stored) return
    this._jsonLoaded = true

    try {
      editor.setEditorState(editor.parseEditorState(stored))
    } catch {
      // A row whose JSON will not parse opens on a sentence saying so rather
      // than throwing, exactly as the React error boundary does.
      const fallback = invalidContentState(this.invalidContentTextValue || "Invalid content.")
      editor.setEditorState(editor.parseEditorState(fallback))
    }

    // The in-place preview was rendered from the server-side value, which on
    // this format is JSON the server cannot turn into HTML. Now that the tree
    // is installed the editor can, so the card stops showing a stale document
    // until the first save.
    if (this.editableInPlaceValue && this.hasPreviewTarget) {
      this.previewTarget.innerHTML = this.previewHTML()
    }
  }

  // The document for the in-place read view. On :lexical_json that is the
  // HTML with each block's alignment and indent put back: both live on the
  // block node and export as `style`, which Lexxy's sanitizer strips, so
  // without this the read view lays every block flush left while the editor
  // shows them where they were set.
  //
  // The reconciler renders one top-level element per root child, so they pair
  // up by position. If the counts disagree the pairing cannot be trusted, and
  // the plain HTML is better than styling the wrong block.
  previewHTML() {
    const html = this.getHTML()
    const editor = this.editorElement?.editor
    if (!this.isLexicalJson || !editor) return html

    let layout = []
    try {
      editor.read(() => {
        layout = (this.rootNode(editor)?.getChildren?.() ?? []).map((block) => ({
          align: block.getFormatType?.() ?? "",
          indent: block.getIndent?.() ?? 0,
        }))
      })
    } catch {
      return html
    }

    const template = document.createElement("template")
    template.innerHTML = html
    const blocks = [...template.content.children]
    if (blocks.length !== layout.length) return html

    const indentStep = this.indentBaseValue()
    blocks.forEach((block, index) => {
      const { align, indent } = layout[index]
      if (align) block.style.textAlign = align
      if (indent > 0) block.style.paddingInlineStart = `calc(${indent} * ${indentStep})`
    })
    return template.innerHTML
  }

  // The step Lexical indents by: the content's `--lexical-indent-base-value`,
  // or Lexical's own 40px when nothing sets it.
  indentBaseValue() {
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    const custom = content ? getComputedStyle(content).getPropertyValue("--lexical-indent-base-value").trim() : ""
    return custom || "40px"
  }

  // Keeps the hidden input in step with the tree. The React editor emits
  // `undefined` for an empty document; the form equivalent is an empty field.
  syncLexicalJson() {
    if (!this.isLexicalJson || !this.hasHiddenTarget) return
    const editor = this.editorElement
    this.hiddenTarget.value = editor && !editor.isEmpty ? (this.getJSON() ?? "") : ""
  }

  // ------------------------------------------------- text formats and fonts

  // Subscript / superscript. Lexxy has no command for either, but the text
  // node's format bitmask does — and unlike alignment these DO survive the
  // HTML value as <sub>/<sup>.
  //
  // Caveat, honestly: without Lexical's selection API this toggles the text
  // node under the caret, not the exact selection. Bold and its siblings do
  // not have that problem because they go through the gem's own commands.
  toggleTextFormat(event) {
    event?.preventDefault?.()
    const format = event?.params?.format
    const bit = TEXT_FORMAT_BITS[format]
    const editor = this.editorElement?.editor
    if (!bit || !editor) return

    editor.update(() => {
      const nodes = this.selectedTextNodes(editor)
      if (nodes.length === 0) return
      // Toggle off the whole run only when every part of it already carries
      // the format — otherwise the click means "make all of this super".
      const allSet = nodes.every((node) => (node.getFormat?.() ?? 0) & bit)
      nodes.forEach((node) => {
        const current = node.getFormat?.() ?? 0
        node.setFormat?.(allSet ? current & ~bit : current | bit)
      })
    }, { discrete: true })
    this.syncLexicalJson()
    this.emit("text-format", { format })
  }

  // Font size and family, through the same node style Lexical serializes.
  applyFontStyle(event) {
    event?.preventDefault?.()
    const property = event?.params?.property
    const value = event?.params?.value
    const editor = this.editorElement?.editor
    if (!property || !value || !editor) return

    editor.update(() => {
      this.selectedTextNodes(editor).forEach((node) => {
        if (!node.setStyle) return
        const kept = (node.getStyle() || "")
          .split(";")
          .map((rule) => rule.trim())
          .filter((rule) => rule && !rule.startsWith(`${property}:`))
        node.setStyle([...kept, `${property}: ${value}`].join("; "))
      })
    }, { discrete: true })

    this.showFontValue(property, value)
    this.syncLexicalJson()
    this.closeDropdownFor(event?.target)
    this.emit("font-style", { property, value })
  }

  showFontValue(property, value) {
    if (!this.hasFontControlTarget) return
    const control = this.fontControlTargets.find((node) => node.dataset.property === property)
    const readout = control?.querySelector("[data-ui--text-editor-target='fontValue']")
    if (readout) readout.textContent = value
  }

  // Check list. `setListType` is a node method the gem never exposed as a
  // command, and the gem already RENDERS the result (role="checkbox" items) —
  // it just ships no styling for it, which lexxy.css supplies here.
  // === THE BLOCK MENU ===
  //
  // A menu, not a row of toggles: whatever the block is, picking an item
  // turns it into that. The gem's commands cannot do this on their own —
  // they toggle, and `setFormatParagraph` only reaches the paragraph already
  // nested inside a quote or a list item, so `Text` left you where you were.
  // Any wrapper is removed first, with its own command, and then the target
  // is applied.
  //
  // The gem's delegated listener on <lexxy-toolbar> answers the same click
  // after this, applying the item's `data-command` to the now-plain block.
  applyBlockFormat(event) {
    const target = event?.params?.format
    if (!target) return

    const current = this.currentBlockFormat()

    if (target === "check") {
      // No command of the gem's makes a check list: it makes a bullet list,
      // and the type is a node write on top. From another list there is
      // nothing to build — a list only has to change type — so unwrapping
      // and rebuilding it would be work for its own sake.
      // Optional call: the gem's delegated listener has nothing to do for the
      // check list, and a caller driving this directly may pass no event.
      event?.preventDefault?.()
      if (!this.isListFormat(current)) {
        this.clearBlockWrapper(current)
        this.runGemCommand("insertUnorderedList")
      }
      this.toggleCheckList()
      return
    }

    // One list kind to another needs no unwrapping — the gem's command
    // converts it in place. Undoing first is worse than useless: run on a
    // check list, `insertUnorderedList` turns it into a bullet list rather
    // than removing it, and the item's own command then removed THAT, so
    // picking Bulleted List from a check list left a bare paragraph.
    const betweenLists = this.isListFormat(current) && this.isListFormat(target)
    if (current !== target && !betweenLists) this.clearBlockWrapper(current)

    // A heading is not a wrapper, so nothing above removes it — and
    // `insertQuoteBlock` would wrap it, leaving `<blockquote><h3>` under a
    // trigger reading "Quote". The menu names one block kind at a time, so
    // the heading goes back to a paragraph before the quote goes on.
    if (target === "quote" && this.isHeadingFormat(current)) {
      this.runGemCommand("setFormatParagraph")
    }

    if (event?.params?.tag) this.correctHeadingTag(event)
  }

  // Quote and the three list kinds WRAP the paragraph; headings and text do
  // not. Only a wrapper has to be undone before the next format goes on, and
  // each undoes itself by running its own command a second time.
  clearBlockWrapper(current) {
    const undo = {
      quote: ["insertQuoteBlock"],
      bullet: ["insertUnorderedList"],
      number: ["insertOrderedList"],
      // Twice, and the order matters: on a CHECK list `insertUnorderedList`
      // converts it to a bullet list rather than removing it, and only the
      // second run takes the list away. One run left the list standing, so
      // picking Quote from a check list wrapped the list in a blockquote
      // instead of replacing it.
      check: ["insertUnorderedList", "insertUnorderedList"]
    }[current]
    undo?.forEach((command) => this.runGemCommand(command))
  }

  isHeadingFormat(format) {
    return format === "h1" || format === "h2" || format === "h3"
  }

  isListFormat(format) {
    return format === "bullet" || format === "number" || format === "check"
  }

  currentBlockFormat() {
    const editor = this.editorElement?.editor
    if (!editor) return "paragraph"

    let key = "paragraph"
    try {
      editor.read(() => {
        const index = this.selectedBlockIndex()
        const block = index == null ? null : this.rootNode(editor)?.getChildren?.()[index]
        key = this.blockFormatKey(block)
      })
    } catch {
      // Before the element upgrades there is no block to read; "paragraph"
      // is the right answer then anyway.
    }
    return key
  }

  // The gem binds one delegated listener to <lexxy-toolbar> and dispatches
  // whatever `data-command` it finds, so a button that exists for the length
  // of a click is a supported way to run a command we have no button for.
  runGemCommand(name) {
    const toolbar = this.editorElement?.querySelector("lexxy-toolbar")
    if (!toolbar) return

    const button = document.createElement("button")
    button.type = "button"
    button.hidden = true
    button.setAttribute("data-command", name)
    toolbar.appendChild(button)
    button.click()
    button.remove()
  }

  toggleCheckList(event) {
    event?.preventDefault?.()
    const editor = this.editorElement?.editor
    if (!editor) return

    editor.update(() => {
      const list = this.caretAncestorOfType(editor, "list")
      if (!list?.setListType) return
      list.setListType(list.getListType?.() === "check" ? "bullet" : "check")
    }, { discrete: true })
    this.syncLexicalJson()
  }

  // The text nodes an inline format should land on — the SELECTION, not the
  // paragraph.
  //
  // The first cut took `block.getChildren()[0]`, which was wrong twice over:
  // it styled the first run of the paragraph whatever the caret was doing,
  // and it styled the whole of it however little was selected. Both are
  // fixable with what the gem actually exposes: `ed.selection
  // .selectedNodeWithOffset()` finds the node under the caret (inside an
  // editor context — outside one it throws), and Lexical's own
  // `TextNode.splitText` is reachable on the node, which is how the selected
  // slice gets isolated before anything is applied to it.
  //
  // Must be called inside `editor.update()`: it splits nodes.
  selectedTextNodes(editor) {
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    const domSelection = document.getSelection()
    const inEditor = content && domSelection?.anchorNode && content.contains(domSelection.anchorNode)

    // No selection of ours to speak of: fall back to the node under the
    // caret, whole. That is the right answer for a collapsed caret — the
    // format then applies to what gets typed next, as it does in any editor.
    if (!inEditor || domSelection.isCollapsed) {
      const caret = this.caretNode()
      return caret ? [caret] : []
    }

    const range = domSelection.getRangeAt(0)
    const index = this.selectedBlockIndex()
    const block = index == null ? null : this.rootNode(editor)?.getChildren?.()[index]
    const children = (block?.getChildren?.() ?? []).filter((node) => node.getType?.() === "text")

    const covered = children.filter((node) => {
      const element = editor.getElementByKey?.(node.getKey())
      // The node's DOM is a <span>/<strong>/… wrapping a text node; the range
      // reports intersection against either.
      return element && (range.intersectsNode(element) || range.intersectsNode(element.firstChild ?? element))
    })
    if (covered.length === 0) return []

    return covered.flatMap((node, position) => {
      const element = editor.getElementByKey?.(node.getKey())
      const text = node.getTextContent?.() ?? ""
      const startsHere = element?.contains(range.startContainer)
      const endsHere = element?.contains(range.endContainer)
      const from = (startsHere && position === 0) ? range.startOffset : 0
      const to = (endsHere && position === covered.length - 1) ? range.endOffset : text.length
      if (from <= 0 && to >= text.length) return [node]

      // splitText returns the pieces in order; the one we want is the piece
      // that starts where the selection does.
      const pieces = node.splitText(...[from, to].filter((offset) => offset > 0 && offset < text.length))
      const wanted = pieces.find((piece) => piece.getTextContent() === text.slice(from, to))
      return wanted ? [wanted] : pieces
    })
  }

  // The node the caret sits in. `selectedNodeWithOffset` throws outside an
  // editor context, so every caller is already inside one.
  caretNode() {
    try {
      return this.editorElement?.selection?.selectedNodeWithOffset?.()?.node ?? null
    } catch {
      return null
    }
  }

  caretAncestorOfType(editor, type) {
    const index = this.selectedBlockIndex()
    if (index == null) return null
    const block = this.rootNode(editor)?.getChildren?.()[index]
    return block?.getType?.() === type ? block : null
  }

  closeDropdownFor(node) {
    const dropdown = node?.closest?.("lexxy-toolbar-dropdown")
    if (dropdown && typeof dropdown.close === "function") dropdown.close()
  }

  // Indent / outdent, the pair the React align menu ends with. Lexical keeps
  // indent on the element node and renders it as padding-inline-start, and it
  // rides the tree, so it persists exactly as alignment does.
  changeIndent(event) {
    event?.preventDefault?.()
    const step = Number(event?.params?.step ?? 0)
    const editor = this.editorElement?.editor
    if (!step || !editor || !this.isLexicalJson) return

    const index = this.selectedBlockIndex()
    if (index == null) return

    editor.update(() => {
      const block = this.rootNode(editor)?.getChildren?.()[index]
      if (typeof block?.setIndent !== "function") return
      block.setIndent(Math.max(0, (block.getIndent?.() ?? 0) + step))
    }, { discrete: true })

    this.syncLexicalJson()
    this.closeDropdownFor(event?.target)
  }

  // === MARKDOWN SHORTCUT: THE CHECK LIST ===
  //
  // The conversion is ours either way — the gem has no check-list command, so
  // the menu item builds one out of a bullet list and a node write — and this
  // runs the same path.
  isCheckListShortcut(text, { inList = false } = {}) {
    return CHECK_LIST_SHORTCUT.test(text) || (inList && CHECK_LIST_SHORTCUT_IN_LIST.test(text))
  }

  maybeCheckListShortcut(event) {
    if (event.key !== " " || event.metaKey || event.ctrlKey || event.altKey) return false

    const context = this.checkListShortcutContext()
    if (!context) return false
    if (!this.isCheckListShortcut(context.typed, { inList: context.inList })) return false

    event.preventDefault()
    this.clearShortcutText()
    this.applyBlockFormat({ params: { format: "check" } })
    this.syncLexicalJson()
    return true
  }

  // The line the caret is on and what has been typed into it so far. A list
  // ITEM when there is one, because that is where the brackets end up once the
  // gem's own dash shortcut has fired; the root-level block otherwise. An item
  // that is already a check item is left alone — the shortcut has nothing to
  // add to it.
  checkListShortcutContext() {
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    const selection = this.editorElement?.ownerDocument?.getSelection?.()
    if (!content || !selection?.rangeCount || !selection.isCollapsed) return null

    const node = selection.anchorNode
    if (!content.contains(node)) return null

    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
    const item = element?.closest?.("li") ?? null
    if (item?.getAttribute("role") === "checkbox") return null

    const block = item ?? [...content.children].find((el) => el.contains(node))
    if (!block) return null

    const range = block.ownerDocument.createRange()
    range.selectNodeContents(block)
    range.setEnd(node, selection.anchorOffset)
    return { typed: range.toString(), inList: !!item }
  }

  // The shortcut's own text goes before the line becomes a check item, or the
  // item opens with `[ ]` already written in it. The text node under the caret
  // is the one holding it, and it is the same one `linkAtCaret` reaches for.
  clearShortcutText() {
    const editor = this.editorElement?.editor
    if (!editor) return

    editor.update(() => {
      const node = this.editorElement?.selection?.selectedNodeWithOffset?.()?.node
      if (typeof node?.setTextContent === "function") node.setTextContent("")
    }, { discrete: true })
  }

  // The gem's heading commands make h2, h3 and h4, whatever the menu item is
  // called. The command runs first and does the real work — turning the block
  // into a heading, which needs a node constructor we cannot reach — and this
  // then corrects the tag to the one the item promised, so picking
  // "Heading 1" leaves an <h1> behind and the trigger agrees.
  //
  // Deferred a tick on purpose: the gem dispatches its command from a
  // delegated listener on the toolbar, and the heading does not exist until
  // that update has run.
  correctHeadingTag(event) {
    const tag = event?.params?.tag
    const editor = this.editorElement?.editor
    if (!tag || !editor) return

    setTimeout(() => {
      editor.update(() => {
        const block = this.caretBlock(editor)
        if (block?.getType?.() !== "heading" || typeof block.setTag !== "function") return
        if (block.getTag?.() !== tag) block.setTag(tag)
      }, { discrete: true })
      this.syncLexicalJson()
      this.refreshToolbarState()
    }, 0)
  }

  // ------------------------------------------------------- toolbar state

  // The React toolbar tracks the selection: marks light up, the block menu
  // names the block you are in, the font readouts show what is actually
  // applied. Ours did none of it — it rendered once and sat there.
  //
  // `editor.registerUpdateListener` fires on every editor update, selection
  // moves included, which is the hook. `ed.selection.getFormat()` answers the
  // marks in one call; sub/superscript and the fonts come off the caret run,
  // because they are node state rather than selection state.
  observeToolbarState() {
    const editor = this.editorElement?.editor
    if (!editor?.registerUpdateListener || this._stopObservingState) return

    this._stopObservingState = editor.registerUpdateListener(() => {
      this.refreshToolbarState()
      this.refreshWordCount()
    })
    this.refreshToolbarState()
  }

  refreshToolbarState() {
    const element = this.editorElement
    const editor = element?.editor
    if (!editor) return

    let state = null
    try {
      editor.read(() => {
        const marks = element.selection?.getFormat?.() ?? {}
        const run = this.caretNode()
        const block = this.caretBlock(editor)
        state = {
          marks,
          textFormat: run?.getFormat?.() ?? 0,
          style: run?.getStyle?.() ?? "",
          block: this.blockFormatKey(block),
          // "" is Lexical's way of saying "no alignment set", which reads as
          // left in a left-to-right document.
          align: block?.getFormatType?.() || "left",
        }
      })
    } catch {
      return // outside an editor context there is nothing to read
    }
    if (!state) return

    this.paintMarks(state)
    this.paintFonts(state.style)
    this.paintBlockFormat(state.block)
    this.paintMenuSelection(state)
  }

  // Marks the chosen item in each menu, the way the React dropdowns do — the
  // current alignment, the current block, the current font. `aria-checked` is
  // the honest attribute for a menu of choices, and the stylesheet paints it.
  paintMenuSelection({ block, align, style }) {
    const declared = Object.fromEntries(
      String(style).split(";").map((rule) => rule.split(":").map((part) => part.trim()))
        .filter(([property, value]) => property && value),
    )

    const mark = (root, selector, matches) => {
      root?.querySelectorAll(selector).forEach((item) => {
        item.setAttribute("aria-checked", String(matches(item)))
      })
    }

    if (this.hasBlockFormatTarget) {
      mark(this.blockFormatTarget, "[data-block-format]",
        (item) => item.getAttribute("data-block-format") === block)
    }
    if (this.hasAlignTarget) {
      // Only the four alignments are a choice; indent/outdent are actions.
      mark(this.alignTarget, "[data-format]", (item) => item.getAttribute("data-format") === align)
    }
    if (this.hasFontControlTarget) {
      this.fontControlTargets.forEach((control) => {
        const property = control.dataset.property
        const current = declared[property] ?? (property === "font-size" ? "14px" : "Inter")
        mark(control, "[data-dropdown-panel] button",
          (item) => item.textContent.trim() === current)
      })
    }
  }

  paintMarks({ marks, textFormat }) {
    const active = {
      bold: !!marks.isBold, italic: !!marks.isItalic,
      underline: !!marks.isUnderline, strikethrough: !!marks.isStrikethrough,
      highlight: !!marks.isHighlight, link: !!marks.isInLink,
      subscript: !!(textFormat & TEXT_FORMAT_BITS.subscript),
      superscript: !!(textFormat & TEXT_FORMAT_BITS.superscript),
    }
    const toolbar = this.editorElement?.querySelector("lexxy-toolbar")
    if (!toolbar) return
    Object.entries(active).forEach(([control, on]) => {
      const button = toolbar.querySelector(`[data-control='${control}']`)
      if (button) button.setAttribute("aria-pressed", String(on))
    })
  }

  // The readouts show what is applied where the caret is, falling back to the
  // defaults when the run carries no style of its own.
  paintFonts(style) {
    if (!this.hasFontControlTarget) return
    const declared = Object.fromEntries(
      String(style).split(";").map((rule) => rule.split(":").map((part) => part.trim()))
        .filter(([property, value]) => property && value),
    )
    this.fontControlTargets.forEach((control) => {
      const property = control.dataset.property
      const readout = control.querySelector("[data-ui--text-editor-target='fontValue']")
      if (!readout) return
      readout.textContent = declared[property] ?? (property === "font-size" ? "14px" : "Inter")
    })
  }

  paintBlockFormat(key) {
    if (!this.hasBlockFormatValueTarget || !this.hasBlockFormatTarget) return
    const item = this.blockFormatTarget.querySelector(`[data-block-format='${key}']`)
    if (item) this.blockFormatValueTarget.textContent = item.textContent.trim()
  }

  caretBlock(editor) {
    const index = this.selectedBlockIndex()
    return index == null ? null : this.rootNode(editor)?.getChildren?.()[index] ?? null
  }

  // Keeps the block menu's trigger naming the block the caret is in, which is
  // what the React editor's does. Runs on every change; cheap, because it only
  // reads the node type.
  refreshBlockFormatLabel() {
    if (!this.hasBlockFormatValueTarget) return
    const editor = this.editorElement?.editor
    if (!editor) return

    let key = "paragraph"
    try {
      editor.read(() => {
        const index = this.selectedBlockIndex()
        const block = index == null ? null : this.rootNode(editor)?.getChildren?.()[index]
        key = this.blockFormatKey(block)
      })
    } catch { return }

    const item = this.hasBlockFormatTarget
      ? this.blockFormatTarget.querySelector(`[data-block-format='${key}']`)
      : null
    if (item) this.blockFormatValueTarget.textContent = item.textContent.trim()
  }

  blockFormatKey(block) {
    if (!block?.getType) return "paragraph"
    const type = block.getType()
    if (type === "quote") return "quote"
    if (type === "heading") return { h1: "h1", h2: "h2", h3: "h3" }[block.getTag?.()] ?? "h3"
    if (type === "list") {
      const listType = block.getListType?.()
      return listType === "check" ? "check" : listType === "number" ? "number" : "bullet"
    }
    return "paragraph"
  }

  // ------------------------------------------------------------- alignment

  // Sets the element format on the block the caret is in. Only reachable on
  // :lexical_json, because `text-align` does not survive Lexxy's HTML.
  //
  // Lexical exposes no way to reach the root node without its own `$getRoot`,
  // which the gem does not export, so this goes through the editor state's
  // node map — the one internal this controller touches. `rootNode()` is the
  // single place that knows, it degrades to a no-op rather than throwing, and
  // `align` is pinned in the vitest suite so a gem bump that moves it fails
  // loudly instead of quietly doing nothing.
  align(event) {
    // Optional call, not just optional chain: `align` is part of the public
    // surface and a consumer may drive it with a plain detail object.
    event?.preventDefault?.()
    const format = event?.params?.format
    const editor = this.editorElement?.editor
    if (!format || !editor || !this.isLexicalJson) return

    const index = this.selectedBlockIndex()
    if (index == null) return

    editor.update(() => {
      const block = this.rootNode(editor)?.getChildren?.()[index]
      if (typeof block?.setFormat === "function") block.setFormat(format)
    }, { discrete: true })

    this.syncLexicalJson()
    // The gem's dropdown closes itself on outside click, not on a menu
    // choice — a picker that stays open after you have picked is a bug.
    if (this.hasAlignTarget && typeof this.alignTarget.close === "function") {
      this.alignTarget.close()
    }
    this.emit("align", { format })
  }

  rootNode(editor) {
    try {
      return editor.getEditorState()._nodeMap?.get("root") ?? null
    } catch {
      return null
    }
  }

  // The reconciler renders root children in order, so the caret's block is
  // found by position rather than by key — no second internal needed.
  selectedBlockIndex() {
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    if (!content) return null

    const blocks = [...content.children]
    if (blocks.length === 0) return null

    const anchor = document.getSelection()?.anchorNode
    if (anchor) {
      const element = anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentElement
      const block = blocks.find((candidate) => candidate === element || candidate.contains(element))
      if (block) return blocks.indexOf(block)
    }
    return 0
  }

  // ------------------------------------------------------ writing direction

  // Moves the component's own toggle into the gem's toolbar. It is rendered in
  // the component's template (icon and label from the design system, not built
  // here) and hidden until there is a toolbar to put it in. It stays inside
  // this controller's element, so `data-action` survives the move.
  //
  // WHERE it lands matters. Lexxy's toolbar ends with a right-hand group
  // (undo/redo, marked `--push-right`) followed by its overflow dropdown, and
  // that dropdown eats controls from the RIGHT as the toolbar narrows. A
  // toggle appended at the end is therefore the first thing to disappear into
  // the "…" menu — invisible on any normal-width pane, which is exactly how
  // this shipped the first time. It goes at the END OF THE FORMATTING CLUSTER
  // instead: before the push-right group, or before the overflow dropdown if
  // the gem ever drops that class, or last if neither is there.
  // Assembles the toolbar: the gem's buttons out, ours in, in the React
  // editor's order.
  //
  // <lexxy-toolbar> stays as the host because the gem delegates clicks on
  // `[data-command]` from that element — our buttons get its commands for
  // free, and a toolbar built anywhere else would look right and do nothing.
  // The gem's own three controls (format menu, link dialog, highlight palette)
  // are not rebuilt: they are moved into their slot, so their behaviour and
  // their panels are untouched.
  placeDirectionToggle() {
    const editor = this.editorElement
    const toolbar = editor?.querySelector("lexxy-toolbar")
    if (!toolbar || !this.hasToolbarSourceTarget) return

    // Everything the gem put there goes, except the three we keep and its own
    // overflow menu, which it fills and shows by itself.
    //
    // The overflow menu has to be walked TOO, not just the bar's own children.
    // The gem compacts a narrow toolbar by moving buttons into that menu and
    // moves them back out when there is room, so anything stashed there when
    // this ran was never hidden and reappeared, unstyled, the moment the bar
    // widened. Below about 700px that was every second control.
    const gemNodes = [
      ...toolbar.querySelectorAll(":scope > *"),
      ...toolbar.querySelectorAll(":scope > .lexxy-editor__toolbar-overflow [data-dropdown-panel] > *")
    ]

    gemNodes.forEach((node) => {
      if (node.classList.contains(OWN_CONTROL_CLASS)) return
      if (node.classList.contains("lexxy-editor__toolbar-overflow")) return
      if (this.gemOwnedSlotFor(node)) {
        node.classList.add(OWN_CONTROL_CLASS, "ui-text-editor__toolbar-adopted")
        return
      }
      node.hidden = true
    })

    // Then our sections, in order, adopting the gem's kept controls where the
    // layout asks for them.
    const source = this.toolbarSourceTarget
    ;[...source.children].forEach((node) => {
      const isSlot = node.getAttribute("data-ui--text-editor-toolbar-part") === "slot"
      const adopted = isSlot ? this.gemControl(toolbar, node.getAttribute("data-control")) : null
      if (isSlot && !adopted) return // the gem never rendered it — skip the gap
      const placed = adopted ?? node
      toolbar.appendChild(placed)
      placed.hidden = false
      if (isSlot) node.remove()
    })

    // The overflow button goes last. When the bar cannot fit everything, the
    // gem gathers the rest under it, and a summary of the row belongs at the
    // end of the row — the gem's own `--push-right` says as much, but that
    // rule is scoped to the gem's toolbar class, which this bar does not
    // carry. Our controls are appended after it, so left alone it ends up
    // FIRST: before undo, at the far left, ahead of what it summarises.
    //
    // Moved in the DOM rather than with flex `order` so that tabbing still
    // follows what the eye sees; the flush-right margin is in lexxy.css.
    const overflow = toolbar.querySelector(":scope > .lexxy-editor__toolbar-overflow")
    if (overflow) toolbar.appendChild(overflow)

    // The gem's kept controls that our layout never asked for stay hidden.
    toolbar.classList.add("ui-text-editor__toolbar")
    source.hidden = true
  }

  // Maps one of the gem's toolbar children to the control name our layout
  // knows it by, or null when it is not one we keep.
  //
  // Which ones we keep is decided by the COMPONENT, which renders a slot for
  // each. Asking the source for that slot rather than holding a list here is
  // what stops the two drifting: the list said `link` for a release after the
  // link control was rebuilt as our own, so the gem's dropdown went on being
  // adopted into a slot that no longer existed and sat, unplaced, at the far
  // left of the bar.
  gemOwnedSlotFor(node) {
    const name = node.getAttribute?.("name")
      || node.querySelector?.("[data-dropdown-trigger][name]")?.getAttribute("name")
    if (!name) return null

    // The gem calls the block menu "format"; our layout calls it "heading".
    const control = { format: "heading" }[name] ?? name
    return this.hasSlotFor(control) ? control : null
  }

  hasSlotFor(control) {
    if (!this.hasToolbarSourceTarget) return false
    return !!this.toolbarSourceTarget.querySelector(
      `[data-ui--text-editor-toolbar-part="slot"][data-control="${control}"]`
    )
  }

  gemControl(toolbar, control) {
    return [...toolbar.children].find((node) => this.gemOwnedSlotFor(node) === control) ?? null
  }

  // ------------------------------------------------- page format and zoom

  // Both are PRESENTATION: a size and a scale on the writing surface, written
  // as custom properties the stylesheet reads. Neither touches the document,
  // which is why they exist here at all — alignment, which does touch it, has
  // no equivalent (Lexxy exposes no command, and Lexical's own element format
  // does not survive `editor.value`).
  // === KEEPING YOUR PLACE ===
  //
  // Page format and zoom both reflow the writing surface, and the browser
  // keeps `scrollTop` as a NUMBER OF PIXELS. Once the text rewraps that same
  // offset is a different point in the document — narrowing A4 to A6 makes
  // the document 74% taller, so the reader is left far earlier than where
  // they were reading.
  //
  // Anchoring to a NODE survives the reflow: remember the block at the top
  // of the viewport and how far into it the fold cuts, then put it back
  // where it was.

  // WHAT SCROLLS depends on the format. Unpaged, the content area is its own
  // scroll container; paged, the content IS the sheet and the element around
  // it is the desk the sheet scrolls inside. Anchoring has to move whichever
  // of the two actually holds the scroll offset.
  scrollSurface() {
    const editor = this.editorElement
    if (!editor) return null
    if (editor.classList.contains("ui-text-editor--paged")) return editor
    return editor.querySelector(".lexxy-editor__content")
  }

  // THE FOLD is where the reader's first line actually starts, which is not
  // the scroll container's top edge: on a paged editor the desk scrolls and
  // the toolbar sticks over the top 48px of it, so a block can be inside the
  // container and still be invisible. Anchoring to the container's top picked
  // one of those, and pinning a mostly-hidden block's top is not the same as
  // keeping the top line — widening A5 to A4 rewraps it to fewer lines and
  // everything below slides up by the difference.
  viewportFold() {
    const surface = this.scrollSurface()
    if (!surface) return null

    const top = surface.getBoundingClientRect().top
    if (surface !== this.editorElement) return top

    const toolbar = this.editorElement.querySelector("lexxy-toolbar")
    if (!toolbar) return top
    return Math.max(top, toolbar.getBoundingClientRect().bottom)
  }

  viewportAnchor() {
    const fold = this.viewportFold()
    const content = this.editorElement?.querySelector(".lexxy-editor__content")
    if (fold === null || !content) return null

    const block = [...content.children].find((el) => el.getBoundingClientRect().bottom > fold + 1)
    if (!block) return null

    // How much of the block the fold has already eaten, both as a distance and
    // as a FRACTION of it. The distance is what to put back when the reader is
    // at the top of a block; the fraction is what to put back when they are in
    // the middle of one, because the reflow changes how many lines it takes and
    // "40px in" then means a different sentence. Pinning the distance there is
    // what dropped a paragraph on every widening: A5's three lines became A4's
    // one, and the 40px that used to be two lines swallowed the whole block.
    const rect = block.getBoundingClientRect()
    const eaten = fold - rect.top
    const ratio = rect.height > 0 ? Math.max(0, Math.min(1, eaten / rect.height)) : 0
    return { block, offset: eaten, ratio }
  }

  restoreViewportAnchor(anchor) {
    if (!anchor?.block?.isConnected) return

    const surface = this.scrollSurface()
    if (!surface) return

    // After layout has settled, not before: the new width has to be applied
    // and the text rewrapped before the block's position means anything.
    requestAnimationFrame(() => {
      if (!anchor.block.isConnected) return
      const fold = this.viewportFold()
      if (fold === null) return
      const rect = anchor.block.getBoundingClientRect()
      const wanted = anchor.ratio > 0 ? anchor.ratio * rect.height : anchor.offset
      surface.scrollTop += (rect.top - fold) + wanted
    })
  }

  changePageFormat(event) {
    const editor = this.editorElement
    if (!editor || !this.hasPageFormatTarget) return

    const anchor = this.viewportAnchor()
    const option = this.pageFormatTarget.selectedOptions[0]
    const width = option?.dataset.width
    const height = option?.dataset.height

    if (width) {
      editor.style.setProperty("--ui-text-editor-page-width", width)
      editor.style.setProperty("--ui-text-editor-page-height", height || "auto")
      editor.classList.add("ui-text-editor--paged")
    } else {
      editor.style.removeProperty("--ui-text-editor-page-width")
      editor.style.removeProperty("--ui-text-editor-page-height")
      editor.classList.remove("ui-text-editor--paged")
    }
    this.applyScale()
    this.restoreViewportAnchor(anchor)
    this.emit("page-format", { format: this.pageFormatTarget.value })
    event?.preventDefault?.()
  }

  zoomIn(event) {
    event?.preventDefault()
    this.applyZoom(this.zoomPercent + ZOOM_STEP)
  }

  zoomOut(event) {
    event?.preventDefault()
    this.applyZoom(this.zoomPercent - ZOOM_STEP)
  }

  resetZoom(event) {
    event?.preventDefault()
    this.applyZoom(ZOOM_DEFAULT)
  }

  applyZoom(percent) {
    const editor = this.editorElement
    if (!editor) return

    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, percent))
    if (next === this.zoomPercent) return

    const anchor = this.viewportAnchor()
    this._zoom = next
    this.applyScale()
    if (this.hasZoomLevelTarget) this.zoomLevelTarget.textContent = `${next}%`
    this.restoreViewportAnchor(anchor)
    this.emit("zoom", { zoom: next })
  }

  get zoomPercent() {
    return this._zoom ?? ZOOM_DEFAULT
  }

  // === FITTING THE SHEET TO THE DESK ===
  //
  // A print preview has to show the whole width of the paper. The React editor
  // gets there by measuring: it scales the page down by
  // `min(1, containerWidth / (pageWidthPx + 80))` and multiplies the reader's
  // zoom on top, so 100% means "the page, whole" on a narrow editor and "the
  // page, actual size" on a wide one. Without it a 210mm sheet runs straight
  // off a 700px editor and the preview is a strip of paper.
  //
  // The product rides the same --ui-text-editor-zoom the zoom buttons write,
  // because the two compose: the stylesheet only ever sees one number.
  fitScale(deskWidth, pageWidth) {
    if (!deskWidth || !pageWidth) return 1
    return Math.min(1, deskWidth / (pageWidth + DESK_GUTTER))
  }

  applyScale() {
    const editor = this.editorElement
    if (!editor) return

    const scale = editor.classList.contains("ui-text-editor--paged")
      ? this.fitScale(editor.clientWidth, this.pageWidthInPixels())
      : 1
    // A unitless number, not a percentage string: CSS `zoom` takes both, but
    // the number is what composes cleanly with the stylesheet's fallback.
    const next = String((this.zoomPercent / 100) * scale)
    if (editor.style.getPropertyValue("--ui-text-editor-zoom") === next) return
    editor.style.setProperty("--ui-text-editor-zoom", next)
  }

  // The format is a CSS length — "210mm", "8.5in" — and only the browser knows
  // what that is in pixels, so ask it the way the React editor does: lay the
  // length out on a throwaway element and read the result back.
  pageWidthInPixels() {
    const editor = this.editorElement
    if (!editor) return 0

    const width = editor.style.getPropertyValue("--ui-text-editor-page-width").trim()
    if (!width) return 0

    const probe = document.createElement("div")
    probe.style.cssText = `position:absolute;visibility:hidden;width:${width}`
    document.body.appendChild(probe)
    const pixels = probe.offsetWidth
    probe.remove()
    return pixels
  }

  // The desk's width changes with the window, and the scale is a function of
  // it, so the fit has to be recomputed rather than measured once. Guarded:
  // jsdom has no ResizeObserver, and a controller that threw here would take
  // the whole editor down with it.
  watchDeskWidth() {
    const editor = this.editorElement
    if (!editor || typeof ResizeObserver === "undefined") return

    this._deskObserver = new ResizeObserver(() => this.applyScale())
    this._deskObserver.observe(editor)
  }

  unwatchDeskWidth() {
    this._deskObserver?.disconnect()
    this._deskObserver = null
  }

  placeInToolbar(button) {
    const editor = this.editorElement
    if (!editor) return

    const toolbar = editor.querySelector("lexxy-toolbar")
    if (!toolbar) return

    const anchor = this.directionToggleAnchor(toolbar, button)
    if (anchor) toolbar.insertBefore(button, anchor)
    else if (button.parentElement !== toolbar) toolbar.appendChild(button)
    button.hidden = false
  }

  // Appends a tool's HTML to the document. It is an APPEND, not an insertion
  // at the caret: Lexxy exposes no selection API to us, so pretending to
  // insert where the cursor is would be a lie. Good enough for what the slot
  // is for — signatures, boilerplate, a date stamp.
  appendSnippet(event) {
    event?.preventDefault()
    const snippet = event?.params?.snippet
    if (!snippet) return
    this.insertHtml(snippet)
  }

  // === THE BUILT-IN TOOL ACTIONS ===
  //
  // The behaviours the React story's external plugins have. Each one is a
  // `Ui::TextEditorComponent::Tool` away for a consumer, rather than a
  // Stimulus controller they have to write against our public API.

  clearContent(event) {
    event?.preventDefault()
    this.clear()
    this.refreshWordCount()
  }

  insertDivider(event) {
    event?.preventDefault()
    this.insertHtml("<hr>")
  }

  // Formatted in the document's own language rather than the browser's, so a
  // Spanish page does not stamp an English date into a Spanish letter.
  insertTimestamp(event) {
    event?.preventDefault()
    const locale = document.documentElement.lang || undefined
    const stamp = new Date().toLocaleString(locale, {
      dateStyle: "long",
      timeStyle: "short",
    })
    this.insertHtml(`<p>${stamp}</p>`)
  }

  // The JSON tree when the field stores one, the HTML otherwise — a consumer
  // on the HTML format has no tree to hand out, and a silent empty file would
  // be worse than an honest one carrying what the field actually holds.
  exportContent(event) {
    event?.preventDefault()

    const json = this.getJSON()
    const payload = JSON.stringify(json ?? { html: this.getHTML() }, null, 2)
    const url = URL.createObjectURL(new Blob([payload], { type: "application/json" }))

    const link = document.createElement("a")
    link.href = url
    // Named after the field, which is the only name this component knows.
    link.download = `${this.nameValue || "document"}.json`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  // The counterpart to the export. The gem's own picker is no use here — it
  // uploads an attachment — so this one is ours, opened on demand rather than
  // parked in the markup, because nothing else needs it.
  importContent(event) {
    event?.preventDefault()

    const input = document.createElement("input")
    input.type = "file"
    input.accept = "application/json,.json"
    input.addEventListener("change", () => this.readImportedFile(input.files?.[0]), { once: true })
    input.click()
  }

  // Both shapes the export writes are accepted: a Lexical tree, and the
  // `{ html }` wrapper it falls back to on an HTML field.
  //
  // A file that will not parse costs nothing: the open document stays and the
  // consumer hears about it. Replacing it with an error state — which is what
  // a bad STORED value does, because there is nothing else to show — would
  // destroy work over someone picking the wrong file.
  async readImportedFile(file) {
    if (!file) return

    let parsed
    let text
    try {
      text = await this.readFileText(file)
      parsed = JSON.parse(text)
    } catch (error) {
      this.emit("error", { reason: "unreadable", message: String(error) })
      return
    }

    if (parsed?.root) {
      const editor = this.editorElement?.editor
      if (!editor?.parseEditorState) {
        this.emit("error", { reason: "unsupported" })
        return
      }
      try {
        editor.setEditorState(editor.parseEditorState(text))
      } catch (error) {
        this.emit("error", { reason: "unreadable", message: String(error) })
        return
      }
      this.syncLexicalJson()
    } else if (typeof parsed?.html === "string") {
      this.setValue(parsed.html)
    } else {
      this.emit("error", { reason: "unrecognised" })
      return
    }

    this.refreshWordCount()
    this.emit("change", { html: this.getHTML(), text: this.getText(), json: this.getJSON() })
  }

  // `File#text` is the short way and is not everywhere — Safari only grew it
  // in 14, and the test environment has no copy either — so FileReader backs
  // it up.
  readFileText(file) {
    if (typeof file.text === "function") return file.text()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(reader.error)
      reader.readAsText(file)
    })
  }

  // At the caret where the gem can place it, appended otherwise — the basic
  // engine has no cursor API to insert into.
  insertHtml(html) {
    const contents = this.editorElement?.contents
    if (contents?.insertHtml) {
      contents.insertHtml(html)
    } else {
      this.setValue(`${this.getHTML()}${html}`)
    }
    this.refreshWordCount()
  }

  // === WORD COUNT ===

  // Counted off the markup with every tag turned into a space, not off the
  // plain text: `<p>two</p><p>three</p>` has no whitespace between the blocks,
  // so reading the text straight would fuse "two" and "three" into one word.
  refreshWordCount() {
    if (!this.hasWordCountTarget) return

    const spaced = this.getHTML().replace(/<[^>]+>/g, " ")
    const decoder = document.createElement("textarea")
    decoder.innerHTML = spaced
    const words = decoder.value.split(/\s+/).filter(Boolean).length

    this.wordCountTarget.textContent = String(words)
  }

  // --------------------------------------------- composable read / write

  beginEdit(event) {
    event?.preventDefault()
    if (!this.editableInPlaceValue || !this.hasEditShellTarget) return

    // The baseline a cancel reverts to. Captured on ENTRY, so it follows each
    // save rather than freezing on whatever the page first loaded with. On
    // :lexical_json it is the tree: the HTML would bring the text back without
    // the alignment and indent it carries.
    this._committedHTML = this.getHTML()
    this._committedJSON = this.getJSON()
    this.editShellTarget.hidden = false
    if (this.hasPreviewTarget) this.previewTarget.hidden = true
    this.focus()
  }

  cancelEdit(event) {
    event?.preventDefault()
    if (!this.editingInPlace) return

    if (this._committedJSON != null) {
      this.restoreJSON(this._committedJSON)
    } else if (this._committedHTML != null) {
      this.setValue(this._committedHTML)
    }
    this.endEdit()
  }

  restoreJSON(json) {
    const editor = this.editorElement?.editor
    if (!editor) return
    editor.setEditorState(editor.parseEditorState(json))
    this.syncLexicalJson()
  }

  save(event) {
    event?.preventDefault()
    if (!this.editingInPlace) return

    const html = this.getHTML()
    this._committedHTML = html
    this._committedJSON = this.getJSON()
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = this.previewHTML()
    this.endEdit()
    // The tree rides along, as it does on `change`. A host on
    // :lexical_json that only got `html` here would be handed exactly the
    // lossy representation the format exists to avoid.
    this.emit("save", { html, text: this.getText(), json: this.getJSON() })
  }

  endEdit() {
    if (this.hasEditShellTarget) this.editShellTarget.hidden = true
    if (this.hasPreviewTarget) this.previewTarget.hidden = false
  }

  get editingInPlace() {
    return this.editableInPlaceValue && this.hasEditShellTarget && !this.editShellTarget.hidden
  }

  directionToggleAnchor(toolbar, button) {
    const candidates = [
      ":scope > .lexxy-editor__toolbar-button--push-right",
      ":scope > .lexxy-editor__toolbar-overflow",
    ]
    for (const selector of candidates) {
      const anchor = toolbar.querySelector(selector)
      if (anchor && anchor !== button) return anchor
    }
    return null
  }

  // Two states, not three: right-to-left on, or off. "Off" removes the
  // attribute rather than writing dir="ltr", which hands every paragraph back
  // to Lexical's per-block detection instead of pinning the whole field to one
  // direction. A host that really must force left-to-right passes
  // `direction: :ltr` and does not offer the toggle.
  toggleDirection(event) {
    event?.preventDefault()
    const editor = this.editorElement
    if (!editor) return

    // A CLASS, not `dir`. `dir` on this element mirrors the toolbar along
    // with the prose, and worse, it never reaches the prose: Lexical stamps
    // every block `dir="auto"`, whose `unicode-bidi: plaintext` ignores an
    // inherited direction. The class drives CSS that sets direction AND
    // unicode-bidi on the blocks, which is the part that actually lands.
    const next = editor.classList.contains(RTL_CLASS) ? "auto" : "rtl"
    editor.classList.toggle(RTL_CLASS, next === "rtl")
    editor.classList.remove(LTR_CLASS)

    if (this.hasDirectionToggleTarget) {
      this.directionToggleTarget.setAttribute("aria-pressed", String(next === "rtl"))
    }
    this.emit("direction", { direction: next })
  }

  // Hides every toolbar control the component did not ask for. Only TOP-LEVEL
  // controls are considered: the buttons inside a dropdown's panel (Normal,
  // Large Heading, …) are menu items, not toolbar controls, and hiding them
  // would empty the menu its allowed trigger opens.
  applyToolbarAllowList() {
    const editor = this.editorElement
    if (!editor) return

    const toolbar = editor.querySelector("lexxy-toolbar")
    if (!toolbar) return

    const allowed = this.hasToolbarValue ? this.toolbarValue : []
    // An empty allow-list means the component asked for no toolbar at all, and
    // the gem was told `toolbar="false"` — so there is nothing to hide, and
    // hiding everything in a toolbar somebody else put there would be wrong.
    if (allowed.length === 0) return

    toolbar.querySelectorAll(":scope > button[name]").forEach((button) => {
      if (button.classList.contains(OWN_CONTROL_CLASS)) return
      button.hidden = !allowed.includes(button.getAttribute("name"))
    })

    toolbar.querySelectorAll(`:scope > :is(${DROPDOWN_TAGS})`).forEach((dropdown) => {
      // The component's own dropdowns live in this toolbar too, and the
      // allow-list is about the GEM's controls. They are exempt by class, not
      // by the absent name, so a future one that needs a name stays safe.
      if (dropdown.classList.contains(OWN_CONTROL_CLASS)) return
      const trigger = dropdown.querySelector("[data-dropdown-trigger][name]")
      // No named trigger means the gem's own overflow menu, which it fills and
      // shows by itself as the toolbar narrows. Never ours to touch.
      if (!trigger) return
      dropdown.hidden = !allowed.includes(trigger.getAttribute("name"))
    })
  }

  // Turbo can insert a <lexxy-editor> that never built itself: the fragment was
  // parsed before the custom element definition loaded, so connectedCallback
  // ran against an undefined element and no editing surface exists. Taking the
  // element out and putting it back re-runs the upgrade. Only a STALLED editor
  // is touched — re-inserting a healthy one would throw away whatever the user
  // has typed.
  reinitializeStalledEditor() {
    const editor = this.editorElement
    if (!editor) return
    if (editor.querySelector("[contenteditable], [role='textbox']")) return

    const parent = editor.parentNode
    if (!parent) return
    const next = editor.nextSibling
    parent.removeChild(editor)
    parent.insertBefore(editor, next)
  }

  emit(name, detail = {}) {
    this.dispatch(name, { detail, prefix: "ui--text-editor" })
  }

  stripTags(html) {
    const div = document.createElement("div")
    div.innerHTML = html ?? ""
    return div.textContent || ""
  }

  // ------------------------------------------- basic engine (DEPRECATED)

  // Toolbar button click handler
  format(event) {
    event.preventDefault()

    if (this.disabledValue) return

    const button = event.currentTarget
    const command = button.dataset.command

    if (command === "link") {
      this.insertLink()
    } else {
      document.execCommand(command, false, null)
    }

    this.editorTarget.focus()
    this.syncHiddenInput()
    this.updateToolbarState()
  }

  // Insert or edit a link
  insertLink() {
    const selection = window.getSelection()
    const selectedText = selection.toString()
    const existingLink = this.getParentLink(selection)

    const url = prompt(
      "Enter URL:",
      existingLink ? existingLink.href : "https://",
    )

    if (url === null) return // Cancelled

    if (url === "") {
      // Remove link
      if (existingLink) {
        document.execCommand("unlink", false, null)
      }
    } else {
      if (selectedText || existingLink) {
        document.execCommand("createLink", false, url)
      } else {
        // No selection: insert the URL as linked text
        document.execCommand(
          "insertHTML",
          false,
          `<a href="${this.escapeHtml(url)}">${this.escapeHtml(url)}</a>`,
        )
      }
    }

    this.syncHiddenInput()
  }

  // Sync editor content to hidden input
  syncHiddenInput() {
    if (!this.hasHiddenTarget || !this.hasEditorTarget) return

    const content = this.editorTarget.innerHTML
    // Treat placeholder-only content as empty
    const isEmpty =
      !this.editorTarget.textContent.trim() &&
      !this.editorTarget.querySelector("img")
    this.hiddenTarget.value = isEmpty ? "" : content
  }

  // Show/hide placeholder based on content
  setupPlaceholder() {
    const editor = this.editorTarget
    const placeholder = editor.dataset.placeholder

    if (!placeholder) return

    const updatePlaceholder = () => {
      const isEmpty = !editor.textContent.trim() && !editor.querySelector("img")
      editor.classList.toggle("is-empty", isEmpty)
    }

    // Add CSS for placeholder via data attribute
    editor.addEventListener("input", () => {
      updatePlaceholder()
      this.syncHiddenInput()
      this.updateToolbarState()
      // Same event shape as the Lexxy engine emits, so a consumer written
      // against the public API works on either engine.
      this.emit("change", { html: this.getHTML(), text: this.getText() })
    })

    editor.addEventListener("focus", () => {
      this.element
        .querySelector("[class*='border']")
        ?.classList.add("ring-2", "ring-primary-500/20")
      this.emit("focus")
    })

    editor.addEventListener("blur", () => {
      updatePlaceholder()
      this.syncHiddenInput()
      this.element
        .querySelector("[class*='border']")
        ?.classList.remove("ring-2", "ring-primary-500/20")
      this.emit("blur")
    })

    // Initial state
    updatePlaceholder()
  }

  // Handle keyboard shortcuts (Ctrl/Cmd + B, I, U, K)
  setupKeyboardShortcuts() {
    this.editorTarget.addEventListener("keydown", (event) => {
      if (this.disabledValue) return

      const isModifier = event.ctrlKey || event.metaKey
      if (!isModifier) return

      const shortcuts = {
        b: "bold",
        i: "italic",
        u: "underline",
        k: "link",
      }

      const command = shortcuts[event.key.toLowerCase()]
      if (!command) return

      event.preventDefault()

      if (command === "link") {
        this.insertLink()
      } else {
        document.execCommand(command, false, null)
      }

      this.syncHiddenInput()
      this.updateToolbarState()
    })
  }

  // Update toolbar button active states based on current selection
  updateToolbarState() {
    if (!this.hasToolbarButtonTargets) return

    this.toolbarButtonTargets.forEach((button) => {
      const command = button.dataset.command

      if (command === "link") {
        const selection = window.getSelection()
        const hasLink = this.getParentLink(selection) !== null
        button.classList.toggle("bg-card-secondary", hasLink)
        button.classList.toggle("text-foreground", hasLink)
        button.setAttribute("aria-pressed", hasLink)
      } else {
        const isActive = document.queryCommandState(command)
        button.classList.toggle("bg-card-secondary", isActive)
        button.classList.toggle("text-foreground", isActive)
        button.setAttribute("aria-pressed", isActive)
      }
    })
  }

  // Find parent <a> element from current selection
  getParentLink(selection) {
    if (!selection || !selection.rangeCount) return null

    let node = selection.anchorNode
    while (node && node !== this.editorTarget) {
      if (node.nodeName === "A") return node
      node = node.parentNode
    }
    return null
  }

  // Escape HTML for safe insertion
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
